/** The API-credentials search namespaces.
 *
 * Same QuerySearch contract as /subscriptions/, but over API_Credential — a
 * SMALL table, so there is no mandatory time window and unbounded searches
 * are fine. `fingerprint` is the safe-for-logging HMAC identifier, `prefix`
 * the first 8 chars of the raw key, `legacy` selects rows with/without a
 * plaintext legacy key, and `expires` supports open bounds (`..X` → before,
 * `X..` → after) for "expiring before X" sweeps.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * (ApiCredentialFilterNode — contract-tested).
 */
import type { NamespaceConfigMap, SuggestionFetcher } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/bookings';
import { makePartnerSuggestionFetcher } from '@/api/partners';

import { CREDENTIAL_SCOPES, CREDENTIAL_STATUSES, CREDENTIAL_TYPES } from './apiCredentialFormat';

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

/** The tenant fetcher plus the pinned `system` sentinel (platform
 *  credentials have no tenant — `tenant=system` compiles to tenant IS NULL). */
function tenantWithSystem(): SuggestionFetcher {
  const fetchTenants = makeSuggestionFetcher('tenant');
  return async (query, extraFilters, signal) => {
    const rows = await fetchTenants(query, extraFilters, signal);
    if (!'system'.startsWith(query.trim().toLowerCase()) && query.trim() !== '') return rows;
    return [
      { value: 'system', label: 'system', description: 'platform credentials (no tenant)' },
      ...rows,
    ];
  };
}

export function buildApiCredentialNamespaces(): NamespaceConfigMap {
  return {
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: tenantWithSystem(),
      help: 'Tour operator (tenant) by code; an all-digits value also matches the tenant id. The literal `system` targets platform credentials with no tenant.',
      examples: ['tenant=curatedplanet', 'tenant=system'],
    },
    name: {
      label: 'name',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Credential name (contains by default; `=` for an exact case-insensitive match).',
      examples: ['name=~import', 'name="Zapier key"'],
    },
    scope: {
      label: 'scope',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(CREDENTIAL_SCOPES),
      help: 'Which API surface accepts the key: client, admin, tour_operator (imports), webhooks, partner_dev (an integration partner’s developer key, minted by its claim link).',
      examples: ['scope=webhooks', 'scope=partner_dev'],
    },
    partner: {
      label: 'partner',
      operators: ['iexact', 'exact'],
      valueType: 'string',
      fetchSuggestions: makePartnerSuggestionFetcher(),
      help: 'The integration partner holding the key (partner_dev keys), by code; an all-digits value also matches the partner id, `=#` pins one partner by id.',
      examples: ['partner=acme', 'partner=#5'],
    },
    status: {
      label: 'status',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(CREDENTIAL_STATUSES),
      help: 'Lifecycle status. Revoked is terminal (also revokes refresh tokens); disabled is reversible.',
      examples: ['status=active', 'status=revoked'],
    },
    type: {
      label: 'type',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(CREDENTIAL_TYPES),
      help: 'Credential type: opaque (pre-shared key) or jwt_refresh (refresh-token child).',
      examples: ['type=opaque'],
    },
    fingerprint: {
      label: 'fingerprint',
      operators: ['iexact'],
      valueType: 'string',
      help: 'The safe-for-logging HMAC key fingerprint (equals, case-insensitive).',
      examples: ['fingerprint=b1946ac92492d234'],
    },
    prefix: {
      label: 'prefix',
      operators: ['iexact'],
      valueType: 'string',
      help: 'First 8 characters of the raw key (equals, case-insensitive).',
      examples: ['prefix=a1b2c3d4'],
    },
    legacy: {
      label: 'legacy',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(['yes', 'no']),
      help: 'yes → rows storing a plaintext legacy key (revealable, permission-gated); no → modern hash-only rows.',
      examples: ['legacy=yes'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the credential was created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-08-20', 'created="2026-08-01 00:00".."2026-08-27 18:00"'],
    },
    expires: {
      label: 'expires',
      operators: ['exact', 'range', 'gt', 'lt'],
      valueType: 'datetime',
      help: 'Expiration, UTC. `=` matches a whole day; open bounds work — `expires=..2026-12-31` finds keys expiring before a date.',
      examples: ['expires=..2026-12-31', 'expires=2026-09-01..'],
    },
    last_used: {
      label: 'last_used',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the key last authenticated a request, UTC (tracked with a 60s debounce). `=` matches a whole day.',
      examples: ['last_used=2026-08-26', 'last_used="2026-08-01 00:00".."2026-08-27 00:00"'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'string',
      help: 'Credential integer id (exact).',
      examples: ['id=123'],
    },
  };
}
